'use client';

import { useRouter } from 'next/navigation';
import { useState, useCallback } from 'react';
import { IndexedDBFileStore } from '../lib/engine/core/file-store';

/**
 * Hook for storing files in IndexedDB and navigating to the app page
 * This replaces the old approach of setting the table directly on the engine
 */
export function useNavigateToViewer() {
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Stores a file in IndexedDB and navigates to the app/viewer page
   * @param params Object containing file and optional source
   * @returns A promise that resolves when the file is stored and navigation begins
   */
  const viewFile = useCallback(async ({ file, source }: { file: File; source?: string }): Promise<void> => {
    setIsProcessing(true);
    setError(null);

    try {
      // Generate a unique fileId
      const fileId = `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      // Create a file store instance
      const fileStore = new IndexedDBFileStore();
      
      // Store the file in IndexedDB - use the fileId as the dataTableSource as well
      // to guarantee uniqueness
      await fileStore.storeFileHandle(fileId, {
        dataTableSource: fileId,
        handle: file
      });
      
      console.log(`File stored in IndexedDB with ID: ${fileId}`);
      
      // Close the file store connection
      await fileStore.close();

      // Build URL with file-id and optional source parameter
      const url = `/app?file-id=${encodeURIComponent(fileId)}${source ? `&source=${encodeURIComponent(source)}` : ''}`;
      router.push(url);
    } catch (err) {
      console.error('Error storing file:', err);
      setError(err instanceof Error ? err.message : 'Failed to store file');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, [router]);

  /**
   * Convenience method to handle FileList objects (e.g., from input[type="file"])
   * @param params Object containing files and optional source
   */
  const viewFiles = useCallback(({ files, source }: { files: FileList | null; source?: string }): Promise<void> => {
    if (!files || files.length === 0) {
      return Promise.resolve();
    }
    
    // Just use the first file
    return viewFile({ file: files[0], source });
  }, [viewFile]);

  /**
   * Navigate directly to the app with a sample dataset ID
   * @param sampleId The ID of the sample dataset to load
   */
  const viewSample = useCallback((sampleId: string): void => {
    setIsProcessing(true);
    
    try {
      // Validate sampleId
      if (!sampleId || typeof sampleId !== 'string') {
        throw new Error('Invalid sample ID');
      }
      
      // Navigate directly to app with the sample-id parameter
      router.push(`/app?sample-id=${encodeURIComponent(sampleId)}`);
    } catch (err) {
      console.error('Error navigating to sample:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sample');
    } finally {
      setIsProcessing(false);
    }
  }, [router]);

  return {
    viewFile,
    viewFiles,
    viewSample,
    isProcessing,
    error
  };
} 